import {ChildRouteNamesFor, MxRoute, MxRouterTabIndexFor, MxRouterTabs, MxRouterTabsFor, MxRoutes, MxRouteTab, PathFor} from './router.types';
import {isPopulatedArray} from '../common/typing.helpers';

export function extractPathFor<RouteNameType extends string = string>(routes: MxRoutes<RouteNameType>, parentPath = ''): PathFor<RouteNameType> {
  return routes.reduce<PathFor<RouteNameType>>(
    (accumulator, route) => {
      const name: RouteNameType = route.name;
      const path: string = route.path ? parentPath + '/' + route.path : parentPath;
      const childRoutes: MxRoutes<RouteNameType> = getChildRoutes(route);

      return {
        ...accumulator,
        [name]: path,
        ...childRoutes.length > 0 && extractPathFor(childRoutes, path)
      };
    },
    Object.create(null)
  );
}

export function extractChildRouteNamesFor<RouteNameType extends string = string>(routes: MxRoutes<RouteNameType>): ChildRouteNamesFor<RouteNameType> {
  return routes.reduce<ChildRouteNamesFor<RouteNameType>>(
    (accumulator, route) => {
      const name: RouteNameType = route.name;
      const childRoutes: MxRoutes<RouteNameType> = getChildRoutes(route);
      const childNames: RouteNameType[] = childRoutes.map(child => child.name);

      return {
        ...accumulator,
        [name]: childNames,
        ...childRoutes.length > 0 && extractChildRouteNamesFor(childRoutes)
      };
    },
    Object.create(null)
  );
}

export function extractRouterTabsFor<RouteNameType extends string = string>(routes: MxRoutes<RouteNameType>): MxRouterTabsFor<RouteNameType> {
  return routes.reduce<MxRouterTabsFor<RouteNameType>>(
    (accumulator, route) => {
      const name: RouteNameType = route.name;
      const tabData: MxRouterTabs<RouteNameType> = extractTabDataFrom(route, name);
      const childRoutes: MxRoutes<RouteNameType> = getChildRoutes(route);

      return {
        ...accumulator,
        [name]: tabData,
        ...childRoutes.length > 0 && extractRouterTabsFor(childRoutes)
      };
    },
    Object.create(null)
  );
}

export function extractRouterTabIndexFor<RouteNameType extends string = string>(routerTabs: MxRouterTabsFor<RouteNameType>): MxRouterTabIndexFor<RouteNameType> {
  return (Object.values(routerTabs) as MxRouterTabs<RouteNameType>[])
    .reduce<MxRouterTabIndexFor<RouteNameType>>(
      (accumulator, tabData) => ({
        ...accumulator,
        ...extractTabIndicesFrom(tabData)
      }),
      Object.create(null)
    );
}

//
// Local functions
//

function getChildRoutes<RouteNameType extends string = string>(route: MxRoute<RouteNameType>): MxRoutes<RouteNameType> {
  return [...getChildrenFrom(route), ...getTabsFrom(route)];
}

function getChildrenFrom<RouteNameType extends string = string>(route: MxRoute<RouteNameType>): MxRoutes<RouteNameType> {
  return 'children' in route && isPopulatedArray(route.children) ? route.children : [];
}

function getTabsFrom<RouteNameType extends string = string>(route: MxRoute<RouteNameType>): readonly MxRouteTab<RouteNameType>[] {
  return 'tabs' in route && isPopulatedArray(route.tabs) ? route.tabs : [];
}

function extractTabDataFrom<RouteNameType extends string = string>(route: MxRoute<RouteNameType>, parentRouteName: RouteNameType): MxRouterTabs<RouteNameType> {
  return getTabsFrom(route).map((tab, index) => ({index, routeName: tab.name, parentRouteName, label: tab.tabLabel}));
}

function extractTabIndicesFrom<RouteNameType extends string = string>(tabData: MxRouterTabs<RouteNameType>): MxRouterTabIndexFor<RouteNameType> {
  return tabData.reduce<MxRouterTabIndexFor<RouteNameType>>(
    (accumulator, tabDatum) => ({
      ...accumulator,
      [tabDatum.routeName]: tabDatum.index
    }),
    Object.create(null)
  );
}
